import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Reveal, Eyebrow, Placeholder, Picture } from "./primitives.jsx";
import { GALLERY } from "../data.js";
import { useSiteContent } from "../content.js";

// A photo falls back to a labeled placeholder if the file is missing or fails
// to decode, so a broken asset never leaves an empty hole in the grid.
function GalImg({ src, alt, label, loading }) {
  const [errored, setErrored] = useState(false);
  if (!src || errored)
    return <Placeholder label={label} ratio="auto" style={{ width: "100%", height: "100%" }} />;
  return <Picture src={src} alt={alt} loading={loading} onError={() => setErrored(true)} />;
}

// A video in the album plays right in the lightbox. It was opened with a
// click, so it can start with its sound on; if the browser still says no, its
// play button is right there. Leaving it (close, next, previous) stops it.
function GalVideo({ src, poster, title }) {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.play()?.catch(() => {});
  }, []);
  return (
    <video
      ref={ref}
      src={src}
      poster={poster}
      controls
      playsInline
      preload="auto"
      aria-label={title}
    />
  );
}

// The poster is the tile's own picture, so it's already loaded (Picture
// serves the .webp).
const posterOf = (img) => img.replace(/\.jpe?g$/i, ".webp");

// unfolded: the whole album open from the start (the gp85 album page).
export default function Gallery({ unfolded = false }) {
  const { gallery: copy } = useSiteContent();
  // `active` is the index into `items` of the open photo, or null when closed.
  const [active, setActive] = useState(null);
  // The whole album is 32 photos: the page shows the first rows and a button
  // for the rest (the hidden ones are still in the page, so the lightbox's
  // next/previous walks all of them and a search engine sees them all).
  const [all, setAll] = useState(unfolded);
  const headRef = useRef(null);
  const toggleAll = () => {
    if (all) headRef.current?.scrollIntoView({ block: "start" });
    setAll((v) => !v);
  };

  // Structural data (image + grid footprint) lives in code; the editable copy
  // (title, category, year, caption) is merged in by id, same split the
  // Collection uses. An item with no matching copy still renders.
  const items = useMemo(() => {
    return GALLERY.map((item) => ({
      ...item,
      ...(copy.items.find((c) => c.id === item.id) || {}),
    }));
  }, [copy.items]);

  const count = items.length;
  const open = active != null ? items[active] : null;
  const close = useCallback(() => setActive(null), []);
  const go = useCallback(
    (delta) => setActive((i) => (i == null ? i : (i + delta + count) % count)),
    [count],
  );

  // Escape closes; left/right arrows step through the photos. Body scroll is
  // locked while the lightbox is open so the page underneath doesn't drift.
  useEffect(() => {
    if (active == null) return;
    const onKey = (e) => {
      // On a video's controls the arrow keys skip through the video instead.
      if (e.target instanceof HTMLMediaElement && e.key.startsWith("Arrow")) return;
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [active, close, go]);

  return (
    <section id="gallery" data-screen-label="The Gallery" className="section gal">
      <div className="section-inner">
        <div className="gal-head" ref={headRef}>
          <Reveal>
            <Eyebrow index="05">{copy.eyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="display h-xl gal-title">
              {copy.titlePrefix} <em>{copy.titleEmphasis}</em>
              <br />
              {copy.titleSuffix}
            </h2>
          </Reveal>
          <Reveal delay={240}>
            <p className="lede" style={{ maxWidth: "60ch" }}>
              {copy.lede}
            </p>
          </Reveal>
        </div>

        <div className={`gal-grid${all ? "" : " is-folded"}`}>
          {items.map((it, i) => (
            <Reveal
              key={it.id}
              delay={Math.min(i * 30, 480)}
              className={`gal-card gal-card-${it.size || "sm"}`}
            >
              <button
                className={`gal-card-btn${it.video ? " is-video" : ""}`}
                onClick={() => setActive(i)}
                aria-label={`${it.video ? "Play video" : "Open photo"}: ${it.title || "Untitled"}`}
              >
                <div className="gal-card-img">
                  <GalImg src={it.img} alt={it.title} label={it.category} />
                </div>
                <div className="gal-card-cap">
                  {it.category ? <span className="gal-card-cat mono">{it.category}</span> : null}
                  <span className="gal-card-title">{it.title}</span>
                </div>
                {it.video ? (
                  <span className="gal-card-play" aria-hidden>
                    <svg viewBox="0 0 24 24">
                      <path d="M8 5.5v13l11-6.5z" fill="currentColor" />
                    </svg>
                    Watch
                  </span>
                ) : (
                  <span className="gal-card-zoom" aria-hidden>
                    +
                  </span>
                )}
              </button>
            </Reveal>
          ))}
        </div>
        {items.length > 8 && (
          <div className="gal-more">
            <button type="button" className="btn btn-ghost" onClick={toggleAll}>
              {all ? "Show fewer" : `See the whole album · ${items.length} photos`}
              <span className="btn-arrow" aria-hidden>
                {all ? "↑" : "↓"}
              </span>
            </button>
          </div>
        )}
      </div>

      {open &&
        createPortal(
          <div className="gal-modal" onClick={close}>
            <button className="gal-modal-close" onClick={close} aria-label="Close" type="button">
              ×
            </button>
            {count > 1 && (
              <button
                className="gal-modal-arrow gal-modal-prev"
                onClick={(e) => {
                  e.stopPropagation();
                  go(-1);
                }}
                aria-label="Previous photo"
                type="button"
              >
                ‹
              </button>
            )}

            <figure className="gal-modal-card" onClick={(e) => e.stopPropagation()}>
              <div className="gal-modal-stage">
                {open.video ? (
                  <GalVideo
                    key={open.id}
                    src={open.video}
                    poster={posterOf(open.img)}
                    title={open.title}
                  />
                ) : (
                  <GalImg src={open.img} alt={open.title} label={open.category} loading="eager" />
                )}
              </div>
              <figcaption className="gal-modal-body">
                <div className="mono gal-modal-eyebrow">
                  {[open.category, open.year].filter((v) => v && v !== "—").join(" · ")}
                  <span className="gal-modal-count">
                    {active + 1} / {count}
                  </span>
                </div>
                <h3 className="display gal-modal-title">{open.title}</h3>
                {open.desc ? <p className="gal-modal-desc">{open.desc}</p> : null}
                <div className="gal-modal-meta mono">FROM THE ALBUM OF GREG PRYOR</div>
              </figcaption>
            </figure>

            {count > 1 && (
              <button
                className="gal-modal-arrow gal-modal-next"
                onClick={(e) => {
                  e.stopPropagation();
                  go(1);
                }}
                aria-label="Next photo"
                type="button"
              >
                ›
              </button>
            )}
          </div>,
          document.body,
        )}
    </section>
  );
}

const galStyles = document.createElement("style");
galStyles.textContent = `
.gal { background: var(--navy-900); }
.gal-head { margin-bottom: 48px; }
.gal-title { color: var(--bone); margin: 24px 0 32px; }
.gal-title em { color: var(--royal-blue-bright); font-style: italic; }

/* Same dense masonry engine the Collection uses, so a mix of portrait,
   landscape, and square photos packs without gaps. */
.gal-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-auto-rows: 200px;
  grid-auto-flow: dense;
  gap: 12px;
}
.gal-card { background: transparent; }
.gal-card-sm   { grid-column: span 1; grid-row: span 1; }
.gal-card-md   { grid-column: span 2; grid-row: span 1; }
.gal-card-lg   { grid-column: span 2; grid-row: span 2; }
.gal-card-tall { grid-column: span 1; grid-row: span 2; }
.gal-card-wide { grid-column: span 2; grid-row: span 1; }

/* Folded: the first eight photos (four full rows of four; seven rows of two
   on a phone). */
.gal-grid.is-folded .gal-card:nth-child(n + 9) { display: none; }
.gal-more { display: flex; justify-content: center; margin-top: 28px; }

@media (max-width: 1080px) {
  .gal-grid { grid-template-columns: repeat(3, 1fr); grid-auto-rows: 190px; }
}
@media (max-width: 640px) {
  .gal-grid { grid-template-columns: repeat(2, 1fr); grid-auto-rows: 168px; gap: 10px; }
  .gal-card-md, .gal-card-lg, .gal-card-wide { grid-column: span 2; grid-row: span 1; }
  .gal-card-tall { grid-column: span 1; grid-row: span 2; }
}
@media (max-width: 430px) {
  .gal-grid { grid-template-columns: repeat(2, 1fr); grid-auto-rows: 150px; }
  .gal-card-lg { grid-row: span 2; }
}

.gal-card-btn {
  display: block;
  width: 100%; height: 100%;
  position: relative;
  padding: 0;
  cursor: pointer;
  border: 1px solid var(--rule);
  background: linear-gradient(135deg, #0e1f37 0%, #1a3658 100%);
  overflow: hidden;
  font-family: inherit;
  transition: transform 300ms cubic-bezier(.2,.8,.2,1), border-color 300ms, box-shadow 300ms;
}
.gal-card-btn:hover {
  transform: translateY(-3px);
  border-color: var(--royal-blue-bright);
  box-shadow: 0 30px 60px -20px rgba(0, 70, 173, 0.5);
}
.gal-card-btn:focus-visible {
  outline: 2px solid var(--royal-blue-glow);
  outline-offset: 2px;
}
.gal-card-img { position: absolute; inset: 0; }
.gal-card-img img {
  width: 100%; height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 600ms cubic-bezier(.2,.8,.2,1);
  filter: saturate(0.9) contrast(1.04);
}
.gal-card-btn:hover .gal-card-img img { transform: scale(1.05); }
.gal-card-img .ph { position: absolute; inset: 0; }

/* Caption rides a bottom gradient — always faintly there, fully legible on
   hover or keyboard focus. The whole tile is one button, so screen readers
   get the title from its aria-label regardless. */
.gal-card-cap {
  position: absolute; left: 0; right: 0; bottom: 0;
  display: flex; flex-direction: column; gap: 2px;
  padding: 28px 12px 10px;
  text-align: left;
  background: linear-gradient(180deg, rgba(5,13,26,0) 0%, rgba(5,13,26,0.82) 100%);
  transform: translateY(8px);
  opacity: 0;
  transition: opacity 260ms, transform 260ms;
  pointer-events: none;
}
.gal-card-btn:hover .gal-card-cap,
.gal-card-btn:focus-visible .gal-card-cap { opacity: 1; transform: translateY(0); }
.gal-card-cat { font-size: 0.5625rem; letter-spacing: 0.18em; color: var(--gold); text-transform: uppercase; }
.gal-card-title {
  font-family: var(--serif); font-size: 0.875rem; font-weight: 500;
  color: var(--bone); line-height: 1.2;
}
.gal-card-zoom {
  position: absolute; top: 10px; right: 10px;
  width: 26px; height: 26px;
  display: grid; place-items: center;
  font-family: var(--mono); font-size: 1rem; line-height: 1;
  color: var(--bone);
  background: rgba(5, 13, 26, 0.7);
  border: 1px solid var(--rule);
  border-radius: 50%;
  opacity: 0;
  transition: opacity 220ms, background 220ms;
}
.gal-card-btn:hover .gal-card-zoom { opacity: 1; }

/* A video's tile: its title card stays whole at the top, and a Watch badge
   says it plays. The caption takes the badge's place on hover. */
.gal-card-btn.is-video .gal-card-img img { object-position: 50% 0; }
.gal-card-play {
  position: absolute; left: 10px; bottom: 10px;
  display: inline-flex; align-items: center; gap: 8px;
  padding: 4px 12px 4px 4px;
  font-family: var(--mono); font-size: 0.625rem; font-weight: 600;
  letter-spacing: 0.16em; text-transform: uppercase; line-height: 1;
  color: var(--bone);
  background: rgba(5, 13, 26, 0.82);
  border: 1px solid rgba(212, 182, 122, 0.55);
  border-radius: 999px;
  transition: opacity 220ms;
}
.gal-card-play svg {
  width: 26px; height: 26px;
  padding: 6px 5px 6px 7px;
  box-sizing: border-box;
  border-radius: 50%;
  background: var(--gold);
  color: var(--navy-900);
}
@media (hover: hover) {
  .gal-card-btn:hover .gal-card-play { opacity: 0; }
}
.gal-card-btn:focus-visible .gal-card-play { opacity: 0; }

/* ---- Lightbox ---- */
.gal-modal {
  position: fixed; inset: 0;
  background: rgba(3, 8, 16, 0.92);
  backdrop-filter: blur(12px);
  z-index: 200;
  display: flex; align-items: center; justify-content: center;
  padding: 32px;
  animation: galFadeIn 220ms;
}
@keyframes galFadeIn { from {opacity:0;} to {opacity:1;} }

.gal-modal-card {
  margin: 0;
  background: var(--navy-800);
  border: 1px solid var(--royal-blue-bright);
  width: min(1080px, 100%);
  max-height: min(90vh, 880px);
  display: flex; flex-direction: column;
  overflow: hidden;
  box-shadow: 0 40px 100px -20px rgba(0,0,0,0.75), 0 0 0 1px rgba(0, 70, 173, 0.4);
  animation: galScaleIn 280ms cubic-bezier(.2,.8,.2,1);
}
@keyframes galScaleIn { from {opacity:0; transform: scale(.97);} to {opacity:1; transform: scale(1);} }

/* Photos vary wildly in shape, so the stage shows the whole frame (contain)
   on a near-black mat rather than cropping it to a fixed window. */
.gal-modal-stage {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: grid; place-items: center;
  background: #05080f;
  padding: 10px;
}
.gal-modal-stage img {
  max-width: 100%;
  max-height: 64vh;
  width: auto; height: auto;
  object-fit: contain;
  display: block;
}
.gal-modal-stage video {
  display: block;
  max-width: 100%;
  max-height: 64vh;
  width: auto; height: auto;
  background: #000;
}
.gal-modal-stage .ph { position: absolute; inset: 0; }

.gal-modal-body {
  flex: 0 0 auto;
  padding: 20px 28px 24px;
  border-top: 1px solid var(--rule);
  background: var(--navy-800);
}
.gal-modal-eyebrow {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  font-size: 0.625rem; letter-spacing: 0.16em; color: var(--gold);
  margin-bottom: 10px;
}
.gal-modal-count { color: var(--bone-dim); opacity: 0.7; }
.gal-modal-title {
  font-family: var(--serif); font-weight: 500;
  font-size: clamp(1.25rem, 2vw, 1.625rem);
  line-height: 1.18; color: var(--bone);
  margin: 0 0 10px; letter-spacing: -0.01em;
}
.gal-modal-desc { font-size: 0.9375rem; line-height: 1.6; color: var(--bone-dim); margin: 0; }
.gal-modal-meta {
  margin-top: 16px; padding-top: 12px;
  font-size: 0.5625rem; letter-spacing: 0.16em; color: var(--gold);
  border-top: 1px solid var(--rule);
}

.gal-modal-close {
  position: fixed; top: 18px; right: 18px;
  width: 42px; height: 42px;
  background: rgba(5, 13, 26, 0.85);
  border: 1px solid var(--rule);
  color: var(--bone);
  font-size: 1.5rem; line-height: 1;
  cursor: pointer; border-radius: 50%;
  z-index: 5;
  transition: background 200ms, border-color 200ms;
}
.gal-modal-close:hover { background: var(--royal-blue-bright); border-color: var(--royal-blue-bright); }

.gal-modal-arrow {
  position: fixed; top: 50%; transform: translateY(-50%);
  width: 48px; height: 48px;
  display: grid; place-items: center;
  background: rgba(5, 13, 26, 0.7);
  border: 1px solid var(--rule);
  color: var(--bone);
  font-size: 2rem; line-height: 1;
  cursor: pointer; border-radius: 50%;
  z-index: 5;
  transition: background 200ms, border-color 200ms;
}
.gal-modal-arrow:hover { background: var(--royal-blue-bright); border-color: var(--royal-blue-bright); }
.gal-modal-prev { left: 18px; }
.gal-modal-next { right: 18px; }

@media (max-width: 760px) {
  .gal-modal { padding: 12px; }
  .gal-modal-card { max-height: 94vh; width: 100%; }
  .gal-modal-stage img, .gal-modal-stage video { max-height: 56vh; }
  .gal-modal-body { padding: 16px 18px 20px; }
  .gal-modal-close { top: 10px; right: 10px; width: 38px; height: 38px; }
  .gal-modal-arrow { width: 40px; height: 40px; font-size: 1.6rem; bottom: 14px; top: auto; transform: none; }
  .gal-modal-prev { left: 14px; }
  .gal-modal-next { right: 14px; }
}
`;
document.head.appendChild(galStyles);
